import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { storeGdprApi, DeletionRequest } from "../../services/gdprApi";
import { Container } from "../../components/ui/Container";
import { Stack } from "../../components/ui/Stack";
import { Heading } from "../../components/ui/Heading";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Spinner } from "../../components/ui/Spinner";
import { Alert } from "../../components/ui/Alert";

export const StoreGdprDashboard: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [deletionLoading, setDeletionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [deletionRequest, setDeletionRequest] = useState<DeletionRequest | null>(null);

  const [deletionForm, setDeletionForm] = useState({
    requestType: "soft_delete" as "soft_delete" | "hard_delete" | "anonymize",
    retentionPeriodDays: 0,
  });

  useEffect(() => {
    if (user) {
      loadDeletionRequests();
    }
  }, [user]);

  const loadDeletionRequests = async (): Promise<void> => {
    // Note: This would require a GET /api/gdpr/store/deletion-requests endpoint
    // For now, we'll show the latest request if available
    // The full implementation would list all requests
  };

  const handleExportData = async (): Promise<void> => {
    try {
      setExporting(true);
      setError(null);
      await storeGdprApi.exportStoreDataAsFile();
      setSuccess(t("gdpr.store.exportSuccess"));
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || t("gdpr.store.exportError"));
      setTimeout(() => setError(null), 5000);
    } finally {
      setExporting(false);
    }
  };

  const handleRequestDeletion = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    try {
      setDeletionLoading(true);
      setError(null);
      const response = await storeGdprApi.requestStoreDeletion({
        requestType: deletionForm.requestType,
        retentionPeriodDays: deletionForm.retentionPeriodDays || undefined,
      });
      setSuccess(t("gdpr.store.deletionRequestSuccess", { 
        requestId: response.requestId 
      }));
      
      // Load the deletion request status
      if (response.requestId) {
        try {
          const request = await storeGdprApi.getStoreDeletionRequest(response.requestId);
          setDeletionRequest(request);
        } catch (err) {
          console.error("Error loading deletion request:", err);
        }
      }

      setDeletionForm({
        requestType: "soft_delete",
        retentionPeriodDays: 0,
      });
      setTimeout(() => {
        setSuccess(null);
        loadDeletionRequests();
      }, 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || t("gdpr.store.deletionRequestError"));
      setTimeout(() => setError(null), 5000);
    } finally {
      setDeletionLoading(false);
    }
  };

  if (!user || (user.role !== "store_admin" && user.role !== "admin" && user.role !== "co_admin")) {
    return (
      <Container>
        <Stack align="center" spacing="lg">
          <Heading size="lg">{t("gdpr.store.loginRequired")}</Heading>
        </Stack>
      </Container>
    );
  }

  return (
    <Container>
      <Stack spacing="lg">
        <Heading size="xl">{t("gdpr.store.dashboardTitle")}</Heading>

        <Alert variant="info" title={t("gdpr.store.accountingNote")}>
          {t("gdpr.store.accountingNoteDescription")}
        </Alert>

        {error && (
          <Card padding="md" className="bg-red-50 border-red-200">
            <p className="text-red-800">{error}</p>
          </Card>
        )}

        {success && (
          <Card padding="md" className="bg-green-50 border-green-200">
            <p className="text-green-800">{success}</p>
          </Card>
        )}

        {/* Data Export Section */}
        <Card padding="md">
          <Stack spacing="md">
            <Heading size="lg">{t("gdpr.store.exportTitle")}</Heading>
            <p className="text-gray-600">
              {t("gdpr.store.exportDescription")}
            </p>
            <Button
              onClick={handleExportData}
              disabled={exporting}
              variant="primary"
            >
              {exporting ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  {t("gdpr.store.exporting")}
                </>
              ) : (
                t("gdpr.store.exportButton")
              )}
            </Button>
          </Stack>
        </Card>

        {/* Store Deletion Section */}
        <Card padding="md">
          <Stack spacing="md">
            <Heading size="lg">{t("gdpr.store.deletionTitle")}</Heading>
            <p className="text-gray-600">
              {t("gdpr.store.deletionDescription")}
            </p>

            <form onSubmit={handleRequestDeletion}>
              <Stack spacing="md">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("gdpr.store.deletionType")}
                  </label>
                  <select
                    value={deletionForm.requestType}
                    onChange={(e) =>
                      setDeletionForm({
                        ...deletionForm,
                        requestType: e.target.value as "soft_delete" | "hard_delete" | "anonymize",
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="soft_delete">
                      {t("gdpr.store.deletionTypeSoft")}
                    </option>
                    <option value="anonymize">
                      {t("gdpr.store.deletionTypeAnonymize")}
                    </option>
                    <option value="hard_delete">
                      {t("gdpr.store.deletionTypeHard")}
                    </option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("gdpr.store.retentionPeriodLabel")}
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="2555"
                    value={deletionForm.retentionPeriodDays}
                    onChange={(e) =>
                      setDeletionForm({
                        ...deletionForm,
                        retentionPeriodDays: parseInt(e.target.value, 10) || 0,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    {t("gdpr.store.retentionPeriodHelp")}
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={deletionLoading}
                  variant="danger"
                >
                  {deletionLoading ? (
                    <>
                      <Spinner size="sm" className="mr-2" />
                      {t("gdpr.store.processing")}
                    </>
                  ) : (
                    t("gdpr.store.requestDeletionButton")
                  )}
                </Button>
              </Stack>
            </form>
          </Stack>
        </Card>

        {/* Deletion Request Status */}
        {deletionRequest && (
          <Card padding="md">
            <Stack spacing="md">
              <Heading size="lg">{t("gdpr.store.deletionRequestStatus")}</Heading>
              <div className="space-y-2">
                <div>
                  <span className="font-medium">{t("gdpr.store.status")}: </span>
                  <span className={`px-2 py-1 rounded text-sm ${
                    deletionRequest.status === "completed" ? "bg-green-100 text-green-800" :
                    deletionRequest.status === "processing" ? "bg-yellow-100 text-yellow-800" :
                    deletionRequest.status === "failed" ? "bg-red-100 text-red-800" :
                    "bg-gray-100 text-gray-800"
                  }`}>
                    {deletionRequest.status}
                  </span>
                </div>
                <div>
                  <span className="font-medium">{t("gdpr.store.requestType")}: </span>
                  {deletionRequest.requestType}
                </div>
                {deletionRequest.scheduledFor && (
                  <div>
                    <span className="font-medium">{t("gdpr.store.scheduledFor")}: </span>
                    {new Date(deletionRequest.scheduledFor).toLocaleString()}
                  </div>
                )}
                {deletionRequest.completedAt && (
                  <div>
                    <span className="font-medium">{t("gdpr.store.completedAt")}: </span>
                    {new Date(deletionRequest.completedAt).toLocaleString()}
                  </div>
                )}
                {deletionRequest.errorMessage && (
                  <div>
                    <span className="font-medium">{t("gdpr.store.error")}: </span>
                    <span className="text-red-600">{deletionRequest.errorMessage}</span>
                  </div>
                )}
              </div>
            </Stack>
          </Card>
        )}
      </Stack>
    </Container>
  );
};

